import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {NbToastrService} from '@nebular/theme';
import {CookieService} from 'ngx-cookie-service';
import {TranslateService} from '@ngx-translate/core';
import {APIService} from './api.service';
import {ClientBilling, CompanyBilling, CostType, Invoice, InvoiceableProject, InvoiceListItem, InvoiceSummary, InvoicingLists, ProjectInvoicing, VatOption} from '../interfaces/invoicing';
import {Moment} from 'moment';
import {Globals} from './Globals';

/** The invoicing settings (#1516, #1517), Admin only, behind the invoicing flag. */
@Injectable({
  providedIn: 'root'
})
export class InvoicingService extends APIService {
  constructor(http: HttpClient, toast: NbToastrService, router: Router, cookie: CookieService,
              private translate: TranslateService) {
    super(http, toast, router, cookie);
  }

  private notice(key: string): string {
    return this.translate.instant('invoicing.notice.' + key);
  }

  // The toast says a refusal in the app's language, like the field under it.
  protected errorText(e: HttpErrorResponse): string | null {
    const ready = refusalText(this.translate, e);
    if (ready) {
      return ready;
    }
    const message = Object.values(e?.error?.errors ?? {})[0]?.[0] ?? e?.error?.message;
    const key = ERROR_KEYS[message];
    return key ? said(this.translate, TOAST_KEYS[key] ?? key, super.errorText(e)) : super.errorText(e);
  }

  async getLists(): Promise<InvoicingLists> {
    return await this.get<InvoicingLists>('/invoicing/lists');
  }

  async getCompany(): Promise<CompanyBilling> {
    return await this.get<CompanyBilling>('/invoicing/company', this.notice('get_company'));
  }

  async saveCompany(company: CompanyBilling): Promise<CompanyBilling> {
    return await this.put<CompanyBilling, CompanyBilling>('/invoicing/company', company);
  }

  async getVatOptions(): Promise<VatOption[]> {
    return await this.get<VatOption[]>('/invoicing/vat-options', this.notice('get_vat_options'));
  }

  async saveVatOption(option: VatOption): Promise<VatOption> {
    return option.id
      ? await this.put<VatOption, VatOption>('/invoicing/vat-options/' + option.id, option, this.notice('vat_option_saved'))
      : await this.post<VatOption, VatOption>('/invoicing/vat-options', option, this.notice('vat_option_saved'));
  }

  async removeVatOption(id: number): Promise<{deleted: boolean}> {
    return await this.delete<{deleted: boolean}>('/invoicing/vat-options/' + id, this.notice('vat_option_deleted'));
  }

  async getKilometers(): Promise<CostType> {
    return await this.get<CostType>('/invoicing/cost-types/kilometers', this.notice('get_kilometers'));
  }

  async saveKilometers(costType: CostType): Promise<CostType> {
    return await this.put<CostType, CostType>('/invoicing/cost-types/kilometers', costType, this.notice('kilometers_saved'));
  }

  async getClient(id: number): Promise<ClientBilling> {
    return await this.get<ClientBilling>('/invoicing/clients/' + id, this.notice('get_billing'));
  }

  async saveClient(id: number, billing: ClientBilling): Promise<ClientBilling> {
    return await this.put<ClientBilling, ClientBilling>('/invoicing/clients/' + id, billing, this.notice('billing_saved'));
  }

  async getProject(id: number): Promise<ProjectInvoicing> {
    return await this.get<ProjectInvoicing>('/invoicing/projects/' + id, this.notice('get_invoicing'));
  }

  async saveProject(id: number, project: ProjectInvoicing): Promise<ProjectInvoicing> {
    return await this.put<ProjectInvoicing, ProjectInvoicing>('/invoicing/projects/' + id, project, this.notice('invoicing_saved'));
  }

  // Invoices (#1518): Admin or the Invoicing grant.
  async getInvoices(projectId: number): Promise<InvoiceSummary[]> {
    return await this.get<InvoiceSummary[]>('/invoicing/projects/' + projectId + '/invoices', this.notice('get_invoices'));
  }

  async createInvoice(projectId: number, from: Moment, to: Moment): Promise<Invoice> {
    return await this.post<object, Invoice>('/invoicing/invoices', {project_id: projectId, from: Globals.toISODate(from), to: Globals.toISODate(to)});
  }

  // The projects a draft can be made for, active first.
  async getInvoiceableProjects(): Promise<InvoiceableProject[]> {
    return await this.get<InvoiceableProject[]>('/invoicing/invoices/projects', this.notice('get_projects'));
  }

  async getInvoice(id: number): Promise<Invoice> {
    return await this.get<Invoice>('/invoicing/invoices/' + id);
  }

  async saveIntro(id: number, intro: string): Promise<Invoice> {
    return await this.put<object, Invoice>('/invoicing/invoices/' + id, {intro});
  }

  async saveLine(id: number, lineId: number, change: {unit_price?: string, description?: string}): Promise<Invoice> {
    return await this.put<object, Invoice>('/invoicing/invoices/' + id + '/lines/' + lineId, change);
  }

  async saveTrip(id: number, entryId: number, included: boolean, reason: string = null): Promise<Invoice> {
    return await this.put<object, Invoice>('/invoicing/invoices/' + id + '/entries/' + entryId, {included, reason});
  }

  async removeInvoice(id: number): Promise<{deleted: boolean}> {
    return await this.delete<{deleted: boolean}>('/invoicing/invoices/' + id, this.notice('draft_deleted'));
  }

  // Also retries the PDF of a final invoice that has none.
  async finalise(id: number): Promise<Invoice> {
    return await this.post<object, Invoice>('/invoicing/invoices/' + id + '/finalise', {});
  }

  // Answers with the credit note, also when the invoice was credited before.
  async credit(id: number): Promise<Invoice> {
    return await this.post<object, Invoice>('/invoicing/invoices/' + id + '/credit', {});
  }

  async downloadInvoice(id: number): Promise<void> {
    await this.saveFile('/invoicing/invoices/' + id + '/pdf', this.notice('get_pdf'), 'invoice.pdf');
  }

  // The e-invoice (UBL XML) for the client's bookkeeping; sending it stays manual.
  async downloadUbl(id: number): Promise<void> {
    await this.saveFile('/invoicing/invoices/' + id + '/ubl', this.notice('get_ubl'), 'invoice.xml');
  }

  // Every invoice of the tenant, drafts included, newest first.
  async getAllInvoices(): Promise<InvoiceListItem[]> {
    return await this.get<InvoiceListItem[]>('/invoicing/invoices', this.notice('get_invoices'));
  }

  // Admin only: the PDF and e-invoice of each final invoice dated in the month (YYYY-MM) and a CSV index, as one ZIP.
  async downloadAll(from: string, to: string): Promise<void> {
    await this.saveFile(`/invoicing/invoices/export?from=${from}&to=${to}`, this.notice('get_zip'), `invoices-${from}_${to}.zip`);
  }

  private async saveFile(url: string, error: string, fallback: string): Promise<void> {
    const response = await this.get<any>(url, error, {responseType: 'blob', observe: 'response'});
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(response.body);
    a.download = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] || fallback;
    a.click();
    window.URL.revokeObjectURL(a.href);
  }
}

/** The field errors of a refused save, keyed by field; empty for any other failure. */
export function fieldErrors(e: HttpErrorResponse): Record<string, string[]> {
  return e?.status === 422 ? e.error?.errors ?? {} : {};
}

// The server's invoicing refusals that have a text in the app's language.
const ERROR_KEYS: Record<string, string> = {
  'A Dutch postcode is four digits and two letters, for example 1234 AB.': 'postcode',
  'The VAT number is NL, nine digits, B and two digits, for example NL123456789B01.': 'company_vat_id',
  'The VAT number starts with its country prefix, for example NL or BE (EL for Greece).': 'client_vat_id',
  'The VAT number needs an EU country prefix, for example NL or BE (EL for Greece).': 'client_vat_id',
  'The KvK number is eight digits.': 'kvk',
  'This IBAN is not valid.': 'iban',
  'The Peppol ID is scheme:identifier, for example 0106:12345678.': 'peppol_id',
  'This scheme is not on the Peppol electronic address list.': 'peppol_id',
  'The payment term is a number of days from 0 to 365.': 'payment_term_days',
  "Reverse charge needs the client's VAT number with an EU country prefix other than NL.": 'reverse_charge',
  'This VAT option was just deleted.': 'vat_gone',
  'This VAT option is in use by a project.': 'vat_in_use',
  'The km price is an amount in euros above zero with at most four decimals, for example 0.23.': 'km_price',
  'Delete the invoice drafts before changing the invoicing start date.': 'starts_on_drafts',
  'The invoicing start date is fixed once an invoice is final.': 'starts_on_fixed',
  'There are no approved hours or chargeable trips on this project in this period.': 'nothing_in_period',
  'This project is not billable.': 'not_billable',
  'Only a trip can be left off an invoice.': 'trips_only',
  'This trip is not chargeable.': 'trip_not_chargeable',
  'A trip on this invoice is no longer chargeable: leave it off first.': 'trip_no_longer_chargeable',
  'The project of this invoice is gone.': 'project_gone',
  'Nothing is left to invoice: every entry is excluded.': 'all_left_off',
  'This invoice has entries before the invoicing start date.': 'before_starts_on',
  'This invoice is final and cannot be changed.': 'final_unchanged',
  'A final invoice is never deleted.': 'final_not_deleted',
  'Only a final invoice with its PDF can be credited.': 'credit_needs_pdf',
  'This whole period is before the invoicing start date.': 'before_start_date',
};

// The field's text introduces the drafts listed under it; the toast stands alone.
const TOAST_KEYS: Record<string, string> = {starts_on_drafts: 'starts_on_drafts_toast'};

/** A field's error in the app's language when it has a text for the message, else the server's. */
// A decimal as the UI language writes it. The server reads either separator.
export function decimalInput(translate: TranslateService, value: string | null | undefined): string {
  return value == null ? '' : (translate.currentLang ?? translate.defaultLang) === 'nl' ? value.replace('.', ',') : value;
}

export function fieldError(translate: TranslateService, errors: Record<string, string[]>, field: string): string | null {
  const message = errors[field]?.[0];
  return message && ERROR_KEYS[message] ? said(translate, ERROR_KEYS[message], message) : message ?? null;
}

// instant() hands back the key itself when it has no text for it; the server's English beats a raw key.
function said(translate: TranslateService, key: string, fallback: string | null): string | null {
  const text = translate.instant('invoicing.error.' + key);
  return text === 'invoicing.error.' + key ? fallback : text;
}

/** A refusal that lists missing settings (InvoicingReadiness::refuse) in the app's language; null for any other refusal. */
export function refusalText(translate: TranslateService, e: HttpErrorResponse): string | null {
  const missing: string[] = e?.error?.missing ?? [];
  return missing.length
    ? translate.instant('invoicing.error.not_ready', {items: missing.map(key => translate.instant('invoicing.missing.' + key)).join(', ')})
    : null;
}

/** Countries by name in the app's language; the list holds ISO codes. */
export function countryNames(codes: string[], lang: string): {code: string, name: string}[] {
  const names = new Intl.DisplayNames([lang || 'en'], {type: 'region'});
  return codes.map(code => ({code, name: names.of(code)})).sort((a, b) => a.name.localeCompare(b.name, lang));
}
